import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipComponent, TooltipEventArgs, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { DEFAULT_SIDEBAR_LOGO_URL } from '../../app/constants/branding.constants';

export type SidebarIconKey =
  | 'home' | 'dashboard' | 'workspace'
  | 'documents' | 'reports' | 'masters';

export interface SidebarNavItem {
  id: string;
  /** i18n key resolved through the `translate` pipe in the template (e.g. `'sidebar.home'`). */
  label: string;
  /** Omitted for sub-items, which render as indented text (no glyph). */
  iconKey?: SidebarIconKey;
  hasChevron?: boolean;
  /** Full router path. Defaults to `/${id}` when omitted. */
  route?: string;
  /**
   * Primary-role codes (`UserService.getRole()`, e.g. `'MGMT'`/`'CADMN'`/`'IMPT'`)
   * allowed to see this item. Omitted / empty ⇒ visible to everyone. Replicates the
   * legacy master page's `user_Roles.Contains(...)` gating (e.g. Masters was
   * management-only). Applied to both parents and children.
   */
  roles?: string[];
  /** Expandable sub-items (e.g. Documents → Critical Documents). */
  children?: SidebarNavItem[];
}

/**
 * Resolves which sub-item a router URL falls under: the child — across every
 * section — whose `route` equals the URL or is a segment-prefix of it (query
 * string / fragment ignored), the LONGEST matching route winning. `null` when
 * the URL sits on no sub-item (e.g. bare `/dashboard`, or a page outside every
 * section).
 *
 * Used by the shell to feed {@link SidebarComponentComponent.activeChildId} on
 * every navigation. URL-derived on purpose — never click-tracked local state:
 * a deep link or browser back/forward must tint the right child too, and the
 * shell's equality-gated `activeId` signal can skip re-emitting when two
 * routes of one section share a `sidebarKey`, so only a per-navigation
 * re-derivation is always current.
 * @param items The sidebar menu (base or derived — only `children[].route` is read).
 * @param url The router URL (`Router.url`).
 * @returns The matching child's id, or `null` when none matches.
 */
export function findActiveSidebarChildId(items: SidebarNavItem[], url: string): string | null {
  const path = url.split('?')[0].split('#')[0];
  let bestId: string | null = null;
  let bestLength = -1;
  for (const item of items) {
    for (const child of item.children ?? []) {
      const route = child.route;
      if (!route) continue;
      if ((path === route || path.startsWith(`${route}/`)) && route.length > bestLength) {
        bestId = child.id;
        bestLength = route.length;
      }
    }
  }
  return bestId;
}

/**
 * The app's base navigation menu. `label` values are i18n KEYS (resolved via
 * `| translate` in the template), not display text — see the en/de dictionaries
 * in `src/app/shared/i18n`.
 *
 * Exported so the shell (AppComponent) can derive the menu it actually binds —
 * it expands the Dashboard entry into a role drop-down per the signed-in user's
 * `get-user-license-roles` payload (`withDashboardSidebarChildren`). The bare
 * Dashboard LEAF here is the fallback when no items are bound.
 */
export const SIDEBAR_NAV_ITEMS: SidebarNavItem[] = [
  { id: 'home',      label: 'sidebar.home',      iconKey: 'home',      hasChevron: false, route: '/home' },
  { id: 'dashboard', label: 'sidebar.dashboard', iconKey: 'dashboard', hasChevron: false },
  { id: 'workspace', label: 'sidebar.workspace', iconKey: 'workspace', hasChevron: false },
  {
    id: 'documents', label: 'sidebar.documents', iconKey: 'documents', hasChevron: true,
    children: [
      { id: 'documents-license',  label: 'sidebar.documentsLicense',  route: '/documents/license'  },
      { id: 'documents-critical', label: 'sidebar.documentsCritical', route: '/documents/critical' },
      /* TODO: remaining Documents sub-categories per Figma —
         Compliance Documents, Task Documents, Act Documents. */
    ],
  },
  { id: 'reports',   label: 'sidebar.reports',   iconKey: 'reports',   hasChevron: false },
  {
    // Management-only section, mirroring the legacy master page's
    // `user_Roles.Contains("MGMT"|"CADMN"|"IMPT")` gate around the Masters menu.
    id: 'masters', label: 'sidebar.masters', iconKey: 'masters', hasChevron: true,
    roles: ['MGMT', 'CADMN', 'IMPT'],
    children: [
      { id: 'masters-license-activation', label: 'sidebar.mastersLicenseActivation', route: '/masters/license-registration' },
      { id: 'masters-license-renew',      label: 'sidebar.mastersLicenseRenew',      route: '/masters/license-renew'      },
      /* User License Type Mapping — Masters screen for authorizing license types per user.
         Visible to all Masters users; no extra access gate beyond the Masters section. */
      { id: 'masters-user-license-type-mapping', label: 'sidebar.mastersLicenseTypeMapping', route: '/masters/user-license-type-mapping' },
    ],
  },
];

@Component({
  selector: 'app-sidebar-component',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
  templateUrl: './sidebar-component.component.html',
  styleUrl: './sidebar-component.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponentComponent implements OnChanges, OnDestroy {
  /** Navigation menu to render. The shell binds the derived menu (base items +
      the Dashboard role drop-down); the base list is the unbound fallback. */
  @Input() items: SidebarNavItem[] = SIDEBAR_NAV_ITEMS;

  /**
   * The signed-in user's primary role code (`UserService.getRole()`), supplied by the
   * shell. Drives which items pass the {@link SidebarNavItem.roles} gate. `null`/empty
   * (e.g. pre-login, SSR) hides every role-gated item — an ungated item is always
   * shown. The sidebar stays presentational: it receives the role, never reads it.
   */
  @Input() role: string | null = null;

  /**
   * {@link items} with role-gated entries removed for the current {@link role}
   * (parents AND children). Recomputed by {@link ngOnChanges} when `items`/`role`
   * change, so the template binds a stable array (no per-CD re-filtering / `*ngFor`
   * churn). Defaults to the full list until a role is bound.
   */
  visibleItems: SidebarNavItem[] = this.items;

  /** Recompute the role-filtered menu whenever `items` or `role` changes, and
      unconditionally drop the flyout: every input change here means navigation
      or a menu rebind (e.g. the shell re-deriving `items` on NavigationEnd, such
      as the Dashboard role children), and an open panel must never keep
      rendering a STALE section object's children just because the rail is still
      collapsed — hover trivially re-opens it. */
  ngOnChanges(): void {
    this.visibleItems = this.computeVisibleItems();
    this.closeFlyout();
  }

  /** True when `item` has no role gate, or the current role is in its allow-list
      (trim + case-insensitive, so `'mgmt '` still matches `'MGMT'`). */
  private isRoleAllowed(item: SidebarNavItem): boolean {
    if (!item.roles?.length) return true;
    const current = (this.role ?? '').trim().toUpperCase();
    return current !== '' && item.roles.some(r => r.trim().toUpperCase() === current);
  }

  /** Filter top-level items by role, and each surviving parent's children too. */
  private computeVisibleItems(): SidebarNavItem[] {
    return this.items
      .filter(item => this.isRoleAllowed(item))
      .map(item =>
        item.children?.length
          ? { ...item, children: item.children.filter(child => this.isRoleAllowed(child)) }
          : item,
      );
  }

  /** Id of the section/route the shell marks active (driven by the router on every
      `NavigationEnd`). Drives the `is-active` row highlight and, via {@link showChildren},
      the DEFAULT expansion of the section whose route is active. It NO LONGER touches the
      manual expand/collapse state ({@link manualOpen}): each section keeps whatever the user
      last toggled it to, so navigating — or opening another section — never changes it. */
  @Input() activeId: string = 'dashboard';

  /**
   * Id of the sub-item the CURRENT ROUTE falls under, or `null` when the route
   * sits on no sub-item. Derived from the URL by the shell on every navigation
   * (see {@link findActiveSidebarChildId}) so deep links and browser
   * back/forward tint the right child too. The matching child keeps the HOVER
   * tint persistently (`.is-selected`) — deliberately the subtle hover colour,
   * NOT the stronger active colour, which the PARENT row alone carries via
   * {@link activeId}.
   */
  @Input() activeChildId: string | null = null;

  @Input() isOpen: boolean = false;

  /** Customer logo (a `data:` URI resolved by the shell from `/common/customer-image`),
      or `null` when the customer has no logo. Presentational only — the sidebar just
      renders it; the shell owns the fetch/cache. When set it replaces the RegTrack/
      License wordmark (expanded) and the "RT" mark (collapsed rail); when null both
      fall back to the default RegTrack logo ({@link defaultLogoUrl}) / {@link defaultMiniLogoUrl}. */
  @Input() logoUrl: string | null = null;

  /** Default RegTrack logo (expanded) — the same hosted asset the compliance app falls back to. */
  readonly defaultLogoUrl = DEFAULT_SIDEBAR_LOGO_URL;

  /** Default collapsed-rail mark, shipped in `public/vectors`. */
  readonly defaultMiniLogoUrl = 'vectors/RTLogo.svg';

  /** True while the shell still does not KNOW which brand belongs here — the customer
      logo has neither resolved nor been ruled out (see `AppComponent.sidebarLogoPending`).
      Takes precedence over {@link logoUrl}: the branding area renders a neutral skeleton
      placeholder rather than the wordmark, so the SSR/prerendered HTML never paints a
      brand that hydration then replaces. Purely presentational — the shell owns the
      state; the sidebar just picks the branch. */
  @Input() logoLoading: boolean = false;

  /** Desktop rail collapse. When true the sidebar shrinks to an icons-only rail
      (labels, chevrons and sub-menus hidden). Driven by the shell so the
      hamburger — and, on wide screens, the top-bar — can toggle it. Ignored below
      the 1024px breakpoint, where the sidebar is a slide-in drawer instead. */
  @Input() collapsed: boolean = false;

  /** Mirrors {@link collapsed} onto the host so `:host(.is-collapsed)` can drive
      the rail width (the width lives on the host, not the inner `.sidebar`). */
  @HostBinding('class.is-collapsed')
  get isCollapsedHostClass(): boolean { return this.collapsed; }

  @Output() navigate    = new EventEmitter<string>();
  @Output() menuToggle  = new EventEmitter<void>();
  @Output() openChange  = new EventEmitter<boolean>();

  /** The delegated rail tooltip (the grids' cmn-grid-tip pattern); its content is
      set per hovered `[data-tip]` target in {@link onRailTooltipBeforeRender}. */
  @ViewChild('railTip') railTip!: TooltipComponent;

  /** OnPush escape hatch for the flyout close timer — a bare setTimeout marks
      nothing dirty, so the timer callback must markForCheck itself. */
  private readonly cdr = inject(ChangeDetectorRef);

  /** Host element, for the outside-click test in {@link onDocumentClick}. */
  private readonly hostRef = inject(ElementRef);

  /** Parent section whose flyout panel is open (collapsed rail only), else null. */
  flyoutItem: SidebarNavItem | null = null;

  /** Layout-px `top` for the fixed-position flyout — set from offsetTop math,
      never getBoundingClientRect (visual px would double-apply --ui-scale). */
  flyoutTop = 0;

  /** Pending close from a mouseleave; ~150ms so crossing row→panel never flickers. */
  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * True when the sidebar is the desktop icons-only rail: `collapsed` AND the
   * ≥1024px media query. `collapsed` alone is not enough — it can stay `true`
   * after a resize below the breakpoint, where the sidebar is the mobile drawer
   * (labels visible, no rail). Only called from event handlers / beforeRender,
   * so it never runs during SSR.
   */
  private isCollapsedRail(): boolean {
    return this.collapsed && window.matchMedia('(min-width: 1024px)').matches;
  }

  /**
   * Show the styled rail tooltip (the global cmn-grid-tip card) only in the
   * collapsed desktop rail, for targets carrying `data-tip` — leaf rows and the
   * hamburger. Parents never match the target selector (they open the flyout
   * instead), and the EXPANDED sidebar shows its labels and carries no tooltips
   * at all (2026-08-12 spec; supersedes the old native-title exception).
   */
  onRailTooltipBeforeRender(args: TooltipEventArgs): void {
    const target = args.target as HTMLElement | null;
    const tip = target?.getAttribute('data-tip')?.trim() ?? '';
    if (!tip || !this.isCollapsedRail()) {
      args.cancel = true;
      return;
    }
    this.railTip.content = tip;
  }

  /**
   * Per-section expand override, keyed by parent id: `true` = the user explicitly opened it,
   * `false` = explicitly collapsed it, ABSENT = no manual interaction (fall back to
   * auto-expanding the section whose route is active). A per-section map — NOT a single
   * "one open at a time" slot — is what makes each section independent: toggling one never
   * changes another, and an override persists across navigation until the user toggles it
   * again (so the active section auto-expanding, or another section opening, can't collapse it).
   */
  private readonly manualOpen = new Map<string, boolean>();

  /**
   * Handle a click on any sidebar row.
   *
   * Parent rows (those with `children`) never navigate — they toggle their OWN
   * sub-menu open/closed and record that choice in {@link manualOpen}. Each section
   * is independent: opening or closing one never affects another (no accordion), and
   * the choice persists until re-toggled. Leaf rows set the active id, emit
   * {@link navigate} with the resolved route, and close the mobile drawer.
   *
   * A SUB-item marks its PARENT active, never itself: every section's routes
   * carry the parent's `sidebarKey` (Documents → `'documents'`, Dashboard role
   * views → `'dashboard'`), so the parent row is what must light. Marking the
   * child here would also STICK: on an intra-section switch (e.g. Dashboard →
   * Performer → Reviewer) the shell re-emits the SAME key, an unchanged signal
   * never re-fires the `activeId` input, and this local child mark would never
   * be corrected — a lit child under a dark parent.
   *
   * @param item The clicked navigation item (parent, leaf, or sub-item).
   * @param parent The section the clicked sub-item belongs to; omitted for
   *   top-level rows.
   *
   * In the collapsed desktop rail a parent click toggles the FLYOUT instead — it
   * never touches {@link manualOpen}.
   */
  onNavigate(item: SidebarNavItem, parent?: SidebarNavItem, event?: Event): void {
    if (item.children?.length) {
      if (this.isCollapsedRail()) {
        /* Collapsed rail: the parent's affordance is the FLYOUT (click is the
           touch fallback for hover-open). NEVER manualOpen here — a hidden
           toggle would silently re-arrange the inline sub-menus the next time
           the rail expands (the pre-2026-08-12 behaviour). */
        if (this.flyoutItem?.id === item.id) {
          this.closeFlyout();
        } else if (event?.currentTarget instanceof HTMLElement) {
          this.cancelFlyoutClose();
          this.openFlyout(item, event.currentTarget);
        }
        return;
      }
      /* Toggle THIS section only: record the opposite of its current visible state.
         Other sections' overrides are untouched, so opening one never collapses another. */
      this.manualOpen.set(item.id, !this.showChildren(item));
      return;
    }
    /* Home never lights itself: the shell sends it to the legacy app (prod) or
       to /dashboard (dev), so its own mark would be wrong — and on a click from a
       dashboard page the shell's unchanged 'dashboard' key never corrects it.
       The shell's activeId input still lights Home if /home is actually reached. */
    if (item.id !== 'home') {
      this.activeId = (parent ?? item).id;
    }
    this.navigate.emit(item.route ?? '/' + item.id);
    if (window.matchMedia('(max-width: 1023.98px)').matches) {
      this.closeDrawer();
    }
  }

  /**
   * Row hover in the collapsed rail: a parent opens its flyout, a leaf closes
   * whatever panel is open. No-op outside the rail, so the expanded sidebar and
   * the mobile drawer keep their inline behaviour.
   */
  onItemEnter(item: SidebarNavItem, event: Event): void {
    if (!this.isCollapsedRail()) return;
    this.cancelFlyoutClose();
    if (item.children?.length) {
      this.openFlyout(item, event.currentTarget as HTMLElement);
    } else {
      this.flyoutItem = null;
    }
  }

  /** Row/panel mouseleave: close after a shared ~150ms grace so moving between
      the row and the panel never flickers the panel shut. */
  onItemLeave(): void {
    if (!this.flyoutItem) return;
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => {
      this.flyoutCloseTimer = null;
      this.flyoutItem = null;
      this.cdr.markForCheck();
    }, 150);
  }

  /** Cancel a pending close — the pointer re-entered the row or the panel. */
  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  /** Immediate close (navigation, Escape, outside click, expand, nav scroll). */
  private closeFlyout(): void {
    this.cancelFlyoutClose();
    this.flyoutItem = null;
  }

  /**
   * Open `item`'s flyout beside its row. `top` is LAYOUT px from the offsetTop
   * chain (row → nav → .sidebar) minus the nav's own scroll — never
   * getBoundingClientRect, which returns VISUAL px that the global --ui-scale
   * body zoom would multiply a SECOND time when written back as CSS `top` (the
   * styles.scss tooltip/column-menu pins document the same trap). The panel is
   * position: fixed so it escapes both overflow clips (.sidebar's
   * overflow: hidden and the scrolling nav).
   */
  private openFlyout(item: SidebarNavItem, row: HTMLElement): void {
    const nav = row.closest<HTMLElement>('.sidebar__nav');
    this.flyoutTop = (nav?.offsetTop ?? 0) + row.offsetTop - (nav?.scrollTop ?? 0);
    this.flyoutItem = item;
  }

  /** Flyout row click: navigate through the normal path, then drop the panel. */
  onFlyoutNavigate(child: SidebarNavItem, parent: SidebarNavItem): void {
    this.closeFlyout();
    this.onNavigate(child, parent);
  }

  /** Close the flyout when the rail scrolls under it — the fixed panel would
      stay behind at its old coordinates otherwise. */
  onNavScroll(): void {
    if (this.flyoutItem) this.closeFlyout();
  }

  /** Any click outside the sidebar host drops the panel. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.flyoutItem) return;
    if (!(this.hostRef.nativeElement as HTMLElement).contains(event.target as Node)) {
      this.closeFlyout();
    }
  }

  /** Reveal a parent's sub-items. Honours this section's OWN explicit user toggle when
      present ({@link manualOpen}); otherwise auto-expands the section whose route is active,
      keeping the active section open by default. Read per-section, so one section's state
      never depends on another's. */
  showChildren(item: SidebarNavItem): boolean {
    if (!item.children?.length) return false;
    const override = this.manualOpen.get(item.id);
    if (override !== undefined) return override;
    return item.id === this.activeId
        || item.children.some(c => c.id === this.activeId);
  }

  closeDrawer(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.openChange.emit(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.closeFlyout();
    this.closeDrawer();
  }

  /**
   * Deviation from the task-2 brief (not in its prescribed code): clear any
   * pending flyout-close timer on destroy. Without this, a mouseleave that
   * scheduled the ~150ms grace-period close outlives the component if the view
   * is torn down first (e.g. a fast navigation right after hovering off a
   * parent row) — the callback then fires against a destroyed component
   * instance. Cheap and unconditionally safe to add.
   */
  ngOnDestroy(): void {
    this.cancelFlyoutClose();
  }
}